import React, { useState, useEffect, useMemo } from 'react';
import { acronymsAPI } from './api';

const CATEGORIES = ['technology', 'product', 'service', 'organization', 'unverified', 'other'];

const PAGE_SIZE = 20;

function AcronymsManagement() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showInactive, setShowInactive] = useState(true);
    const [page, setPage] = useState(1);

    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        acronym: '', expansion: '', category: 'technology', is_active: true
    });
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            setLoading(true);
            const res = await acronymsAPI.getAll(false);
            setRows(res.data?.data || []);
        } catch (err) {
            console.error('Failed to load acronyms:', err);
            alert('Gagal memuat acronyms: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r => {
            if (!showInactive && !r.is_active) return false;
            if (!q) return true;
            return (
                String(r.acronym).toLowerCase().includes(q) ||
                String(r.expansion).toLowerCase().includes(q) ||
                String(r.category || '').toLowerCase().includes(q)
            );
        });
    }, [rows, search, showInactive]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

    const openCreate = () => {
        setEditing(null);
        setFormData({ acronym: '', expansion: '', category: 'technology', is_active: true });
        setFormError(null);
        setShowForm(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setFormData({
            acronym: row.acronym, expansion: row.expansion,
            category: row.category || 'other', is_active: !!row.is_active,
        });
        setFormError(null);
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditing(null);
        setFormError(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        setFormError(null);
        const acr = formData.acronym.trim().toUpperCase();
        const exp = formData.expansion.trim();
        if (!acr) return setFormError('Acronym wajib diisi');
        if (!exp) return setFormError('Expansion wajib diisi');

        setSubmitting(true);
        try {
            const payload = { acronym: acr, expansion: exp, category: formData.category, is_active: formData.is_active };
            if (editing) await acronymsAPI.update(editing.id, payload);
            else         await acronymsAPI.create(payload);
            closeForm();
            await load();
        } catch (err) {
            const msg = err.response?.data?.error || err.message;
            setFormError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const softDelete = async (row) => {
        if (!window.confirm(`Nonaktifkan acronym "${row.acronym} → ${row.expansion}"?`)) return;
        try {
            await acronymsAPI.update(row.id, { is_active: false });
            await load();
        } catch (err) {
            alert('Gagal nonaktifkan: ' + (err.response?.data?.error || err.message));
        }
    };

    const reactivate = async (row) => {
        try {
            await acronymsAPI.update(row.id, { is_active: true });
            await load();
        } catch (err) {
            alert('Gagal aktifkan: ' + (err.response?.data?.error || err.message));
        }
    };

    return (
        <div style={page_wrap}>
            <div style={header_box}>
                <div>
                    <h1 style={h1}>Acronyms</h1>
                    <p style={sub}>Kelola dictionary singkatan yang dipakai chatbot saat memperluas query.</p>
                </div>
                <button onClick={openCreate} style={btnPrimary}>+ Tambah Acronym</button>
            </div>

            <div style={controls_row}>
                <input
                    type="text"
                    placeholder="Cari acronym, expansion, atau category..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    style={searchInput}
                />
                <label style={toggleLabel}>
                    <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                    <span style={{ marginLeft: 6 }}>Tampilkan inactive</span>
                </label>
                <span style={countBadge}>{filtered.length} entry</span>
            </div>

            <div style={card}>
                <table style={table}>
                    <thead>
                        <tr style={trHead}>
                            <th style={{ ...th, width: 60 }}>ID</th>
                            <th style={{ ...th, width: 120 }}>Acronym</th>
                            <th style={th}>Expansion</th>
                            <th style={{ ...th, width: 140 }}>Category</th>
                            <th style={{ ...th, width: 90 }}>Status</th>
                            <th style={{ ...th, width: 200, textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (<tr><td colSpan="6" style={tdEmpty}>Memuat...</td></tr>)}
                        {!loading && pageRows.length === 0 && (<tr><td colSpan="6" style={tdEmpty}>Tidak ada data.</td></tr>)}
                        {!loading && pageRows.map(r => (
                            <tr key={r.id} style={tr}>
                                <td style={td}>{r.id}</td>
                                <td style={{ ...td, fontWeight: 700, color: '#a30021' }}>{r.acronym}</td>
                                <td style={td}>{r.expansion}</td>
                                <td style={td}>
                                    <span style={catChip(r.category)}>{r.category || '—'}</span>
                                </td>
                                <td style={td}>
                                    <span style={statusChip(r.is_active)}>{r.is_active ? 'Active' : 'Inactive'}</span>
                                </td>
                                <td style={{ ...td, textAlign: 'right' }}>
                                    <button onClick={() => openEdit(r)} style={btnGhost}>Edit</button>
                                    {r.is_active
                                        ? <button onClick={() => softDelete(r)} style={btnDanger}>Nonaktifkan</button>
                                        : <button onClick={() => reactivate(r)} style={btnGhost}>Aktifkan</button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div style={pager}>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={btnPager}>‹ Prev</button>
                    <span style={{ alignSelf: 'center' }}>Halaman {page} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnPager}>Next ›</button>
                </div>
            )}

            {showForm && (
                <div style={modalBackdrop} onClick={closeForm}>
                    <div style={modalBox} onClick={(e) => e.stopPropagation()}>
                        <h2 style={h2}>{editing ? 'Edit Acronym' : 'Tambah Acronym'}</h2>
                        <form onSubmit={submit}>
                            <label style={label}>Acronym *
                                <input
                                    style={input}
                                    type="text"
                                    value={formData.acronym}
                                    onChange={e => setFormData({ ...formData, acronym: e.target.value.toUpperCase() })}
                                    placeholder="contoh: SBS"
                                    maxLength={50}
                                    required
                                />
                            </label>
                            <label style={label}>Expansion *
                                <input
                                    style={input}
                                    type="text"
                                    value={formData.expansion}
                                    onChange={e => setFormData({ ...formData, expansion: e.target.value })}
                                    placeholder="contoh: Starlink Business Services"
                                    maxLength={255}
                                    required
                                />
                            </label>
                            <label style={label}>Category
                                <select
                                    style={input}
                                    value={formData.category}
                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                >
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </label>
                            <label style={{ ...label, flexDirection: 'row', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                <span style={{ marginLeft: 8 }}>Active</span>
                            </label>

                            {formError && <div style={errorBox}>{formError}</div>}

                            <div style={modalFooter}>
                                <button type="button" onClick={closeForm} style={btnGhost}>Batal</button>
                                <button type="submit" disabled={submitting} style={btnPrimary}>
                                    {submitting ? 'Menyimpan...' : (editing ? 'Update' : 'Tambah')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- styles (inline, matching dashboard glassmorphism + dark-red theme) ----------
const page_wrap = { padding: '32px', maxWidth: 1200, margin: '0 auto' };
const header_box = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 16 };
const h1 = { fontSize: 28, fontWeight: 700, margin: 0, color: '#1a1a1a' };
const sub = { color: '#666', margin: '6px 0 0 0', fontSize: 14 };
const h2 = { fontSize: 20, fontWeight: 600, marginTop: 0, marginBottom: 16 };

const controls_row = { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' };
const searchInput = { flex: 1, minWidth: 240, padding: '10px 14px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14, background: '#fff' };
const toggleLabel = { fontSize: 13, color: '#555', display: 'flex', alignItems: 'center' };
const countBadge = { padding: '6px 12px', background: '#f5f5f5', borderRadius: 6, fontSize: 13, color: '#444', fontWeight: 600 };

const card = { background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.4)' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const trHead = { background: 'linear-gradient(135deg, #700000, #a30021)' };
const th = { padding: '14px 16px', textAlign: 'left', color: '#fff', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' };
const tr = { borderBottom: '1px solid #f0f0f0' };
const td = { padding: '14px 16px', color: '#333', verticalAlign: 'middle' };
const tdEmpty = { padding: '32px 16px', textAlign: 'center', color: '#999' };

const catChip = (cat) => {
    const colors = {
        technology: { bg: '#e3f2fd', fg: '#1565c0' },
        product:    { bg: '#e8f5e9', fg: '#2e7d32' },
        service:    { bg: '#fff3e0', fg: '#e65100' },
        organization: { bg: '#f3e5f5', fg: '#6a1b9a' },
        unverified: { bg: '#ffebee', fg: '#c62828' },
        other:      { bg: '#eceff1', fg: '#37474f' },
    };
    const c = colors[cat] || colors.other;
    return { padding: '4px 10px', borderRadius: 12, background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600 };
};
const statusChip = (active) => ({
    padding: '4px 10px', borderRadius: 12,
    background: active ? '#e8f5e9' : '#eceff1',
    color: active ? '#2e7d32' : '#757575',
    fontSize: 12, fontWeight: 600,
});

const btnPrimary = { padding: '10px 18px', background: 'linear-gradient(135deg, #700000, #a30021)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14, boxShadow: '0 4px 12px rgba(163, 0, 33, 0.25)' };
const btnGhost   = { padding: '8px 14px', background: 'transparent', color: '#444', border: '1px solid #d0d0d0', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginLeft: 8 };
const btnDanger  = { padding: '8px 14px', background: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginLeft: 8, fontWeight: 600 };
const btnPager   = { padding: '8px 16px', background: '#fff', border: '1px solid #d0d0d0', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const pager = { display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 };

const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBox = { background: '#fff', borderRadius: 12, padding: 28, width: '90%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const label = { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, fontSize: 13, color: '#555', fontWeight: 500 };
const input = { padding: '10px 12px', borderRadius: 6, border: '1px solid #d0d0d0', fontSize: 14, background: '#fff' };
const errorBox = { padding: '10px 12px', background: '#ffebee', color: '#c62828', borderRadius: 6, fontSize: 13, marginBottom: 12 };
const modalFooter = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 };

export default AcronymsManagement;
